import { asPatientId, mintEntityId } from "@repo/domain";
import { Button } from "@repo/ui/components/ui/button";
import { DialogFooter } from "@repo/ui/components/ui/dialog";
import { Label } from "@repo/ui/components/ui/label";
import { endOfDay, isToday, startOfDay } from "date-fns";
import { Clock } from "lucide-react";
import * as React from "react";
import { ProgramOptionPicker } from "@/features/catalog";
import {
  DEFAULT_PROTOCOLS_BOOKING_FORM_VALUES,
  type ProtocolsBookingFormValues,
  useAppForm,
  useProtocolsBookingFormData,
} from "@/features/scheduling/forms/use-protocols-booking-form";
import { combineDateTimeISO } from "@/features/scheduling/lib/appointment-utils";
import { focusFirstInvalid } from "@/shared/forms/focus-first-invalid";
import type { BookingPage } from "./booking-store";

/**
 * No practitioner prop and no schedule prop. A protocol visit is delivered by
 * the patient's care manager and the server reads that off the active case, so
 * the calendar to search is a consequence of the patient, not a separate choice
 * (ADR-0019).
 */
interface ProtocolsFlowProps {
  page: BookingPage;
  initialDate?: Date;
  initialTime?: string;
  onClose: () => void;
  onNext: () => void;
  onBack: () => void;
}

export function ProtocolsFlow({ page, initialDate, initialTime, onClose, onNext, onBack }: ProtocolsFlowProps) {
  const {
    patients,
    protocolOptions,
    isLoadingPatients,
    isPending,
    defaultValues,
    validators,
    handleSubmit,
    slots,
    isLoadingAvailability,
    availabilityError,
    queryCandidates,
  } = useProtocolsBookingFormData({ initialDate, initialTime, onSuccess: onClose });

  const lastFetchRef = React.useRef("");

  const form = useAppForm({
    defaultValues: defaultValues as ProtocolsBookingFormValues,
    validators,
    onSubmitInvalid: focusFirstInvalid,
    listeners: {
      onChange: ({ formApi }) => {
        const { date, patientId, protocol } = formApi.state.values;
        if (!date || !patientId || !protocol) return;
        const key = JSON.stringify([patientId, date.toISOString(), protocol.protocolUrl, protocol.protocolVersion]);
        if (key === lastFetchRef.current) return;
        lastFetchRef.current = key;
        queryCandidates({
          orderSetId: mintEntityId(),
          patientId: asPatientId(patientId),
          protocolUrl: protocol.protocolUrl,
          protocolVersion: protocol.protocolVersion,
          start: (isToday(date) ? new Date() : startOfDay(date)).toISOString(),
          end: endOfDay(date).toISOString(),
        });
      },
    },
    onSubmit: async ({ value }) => {
      await handleSubmit(value);
    },
  });

  React.useEffect(() => {
    lastFetchRef.current = "";
    form.reset({
      ...DEFAULT_PROTOCOLS_BOOKING_FORM_VALUES,
      date: initialDate ?? null,
      startTime: initialTime && initialDate ? combineDateTimeISO(initialDate, initialTime) : "",
      endTime: "",
    });
  }, [initialDate, initialTime, form]);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        form.handleSubmit();
      }}
      className="space-y-4"
    >
      {/* Page 1: Form fields */}
      <div className={page !== "protocols.form" ? "hidden" : undefined}>
        <div className="space-y-4">
          <form.AppField name="patientId">
            {(field) => <field.PatientField label="Patient" patients={patients} isLoading={isLoadingPatients} />}
          </form.AppField>

          <form.AppField
            name="protocol"
            listeners={{
              onChange: () => {
                form.setFieldValue("startTime", "");
                form.setFieldValue("endTime", "");
              },
            }}
          >
            {(field) => {
              const selectedProtocol = field.state.value;

              return (
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <Label>Protocol</Label>
                    {selectedProtocol && (
                      <span className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Clock className="size-3" />
                        {selectedProtocol.durationMinutes} min
                      </span>
                    )}
                  </div>

                  <ProgramOptionPicker
                    value={selectedProtocol ?? undefined}
                    onChange={(protocol) => field.handleChange(protocol ?? null)}
                    options={protocolOptions}
                  />
                </div>
              );
            }}
          </form.AppField>

          <form.AppField
            name="date"
            listeners={{
              onChange: () => {
                form.setFieldValue("startTime", "");
                form.setFieldValue("endTime", "");
              },
            }}
          >
            {(field) => <field.DateField label="Start Date" fromDate={new Date()} />}
          </form.AppField>
        </div>
      </div>

      {/* Page 1 footer */}
      {page === "protocols.form" && (
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onBack}>
            Back
          </Button>
          <form.Subscribe selector={(s) => !!(s.values.patientId && s.values.protocol && s.values.date)}>
            {(canAdvance) => (
              <Button type="button" disabled={!canAdvance} onClick={onNext}>
                Next
              </Button>
            )}
          </form.Subscribe>
        </DialogFooter>
      )}

      {/* Page 2: Slot picker */}
      <div className={page !== "protocols.slots" ? "hidden" : undefined}>
        <form.Subscribe selector={(s) => ({ protocol: s.values.protocol, date: s.values.date })}>
          {({ protocol, date }) => {
            if (!date || !protocol) return null;

            return (
              <div className="space-y-3">
                <form.AppField name="startTime">
                  {(field) => (
                    <field.AvailabilitySlotPicker
                      label="Available Times"
                      slots={slots}
                      isLoading={isLoadingAvailability}
                      error={availabilityError}
                      onSlotSelect={(slot) => {
                        form.setFieldValue("endTime", slot.endTime);
                      }}
                    />
                  )}
                </form.AppField>
              </div>
            );
          }}
        </form.Subscribe>
      </div>

      {/* Page 2 footer */}
      {page === "protocols.slots" && (
        <DialogFooter className="flex-col items-stretch gap-2 sm:flex-row sm:items-center">
          <Button type="button" variant="outline" onClick={onBack} disabled={isPending}>
            Back
          </Button>
          <form.Subscribe
            selector={(s) =>
              !!(s.values.patientId && s.values.protocol && s.values.date && s.values.startTime && s.values.endTime)
            }
          >
            {(hasRequiredFields) => (
              <Button type="submit" disabled={!hasRequiredFields || isPending}>
                {isPending ? "Booking..." : "Book Appointment"}
              </Button>
            )}
          </form.Subscribe>
        </DialogFooter>
      )}
    </form>
  );
}
